import { useQueryClient } from '@tanstack/react-query';
import { T, useTranslate } from '@tolgee/react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { CheckSvg } from '../../../assets/icons';
import DisabledSvg from '../../../assets/icons/DisabledSvg';
import UndoSvg from '../../../assets/icons/UndoSvg';
import theme from '../../../assets/theme';
import { AiAssessedScore } from '../../../frontendServices/gameAPI';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useImagePreview from '../../../hooks/useImagePreview';
import useListAllAiAssessedScores from '../../../hooks/useListAllAiAssessedScores';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { SocketContext } from '../../../providers/SocketProvider/SocketProvider';
import {
  ById,
  PartialParticipant,
  Response,
} from '../../../services/backendService/types';
import { RoundState } from '../../../utils/roundUtils';
import { SlideType } from '../../SlideComponents/slide.types';
import AiAssessedAnswersListItem from '../AiAssessedAnswersListItem';
import { Button } from '../Button';
import { DrawingImageGridItem } from '../DismissableQuestionList/DismissableQuestionList';

const ListItem = ({
  response,
  participantsById,
  participantIsFinishedStatus,
  onDismiss,
  slideType,
  requireRealNames,
  aiAssessedScore,
  isFetchingAiAssessedScores,
  gameId,
  slideId,
}: {
  slideType: SlideType;
  response: Response;
  participantIsFinishedStatus:
    | 'writing'
    | 'done'
    | 'notStarted'
    | 'absent'
    | 'dismissed'
    | 'feedbackLoading'
    | 'feedbackReady';
  requireRealNames?: boolean;
  participantsById: ById<PartialParticipant>;
  onDismiss: (data: { responseId: string; dismiss: boolean }) => void;
  aiAssessedScore?: AiAssessedScore;
  isFetchingAiAssessedScores: boolean;
  gameId: string;
  slideId: string;
}) => {
  const { t } = useTranslate();
  const isLargerThan650 = useBreakPoint(650);
  const isDismissed = response.dismissed;
  const participant = participantsById[response.uid];

  const splitResponse = useMemo(() => response.response.split('\n'), [response.response]);

  const { imagePreviewComponent, onImageClicked } = useImagePreview('image');

  return (
    <ResponseItemContainer
      $dismissed={isDismissed}
      $isAiAssessedAnswers={
        slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers'
      }
    >
      {imagePreviewComponent}
      <ResponseWrapper
        $isAiAssessedAnswers={
          slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers'
        }
        style={{
          gridTemplateColumns:
            (slideType === 'aiAssessedAnswers' ||
              slideType === 'aiAssessedImageAnswers') &&
            !!aiAssessedScore &&
            isLargerThan650
              ? '1fr 1fr'
              : '1fr',
        }}
      >
        <ResponseTextContainer
          $dismissed={isDismissed}
          $isAiAssessedAnswers={
            slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers'
          }
          $aiAssessedScore={aiAssessedScore}
        >
          <HorizontalContainer>
            <PlaceMobile>
              <span>
                {/* Show real name */}
                {participant &&
                  requireRealNames &&
                  (participant.name || participant.nickName)}
                {/* Show nick name */}
                {participant && !requireRealNames && participant.nickName}
              </span>
            </PlaceMobile>
            {!isDismissed && slideType !== 'poll' && (
              <Button
                onClick={() => onDismiss({ responseId: response.id, dismiss: true })}
                icon={<DisabledSvg />}
                height="32px"
                styles={{ color: '#c44137', borderColor: '#c44137', padding: '0px 5px' }}
                type="tertiary"
              >
                {t('Dismiss')}
              </Button>
            )}
            {isDismissed && (
              <Button
                icon={<UndoSvg />}
                height="32px"
                type="tertiary"
                styles={{ color: theme.colors.black, borderColor: theme.colors.black }}
                onClick={() => onDismiss({ responseId: response.id, dismiss: false })}
              >
                {t('Undo')}
              </Button>
            )}
          </HorizontalContainer>

          <>
            {slideType === 'aiAssessedImageAnswers' && (
              <QuestionMobile $borderColor={'transparent'}>
                <DrawingImageGridItem
                  isAuthenticated={true}
                  response={response}
                  onClick={onImageClicked}
                />
              </QuestionMobile>
            )}
            {slideType !== 'aiAssessedImageAnswers' && (
              <QuestionMobile>
                <div
                  style={{
                    fontSize: response.response.length <= 300 ? '18px' : '16px',
                  }}
                >
                  {splitResponse.map((r, i) => (
                    <p
                      style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                      key={i}
                    >
                      {r}
                    </p>
                  ))}
                </div>
                <p
                  style={{
                    textAlign: 'right',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    width: '100%',
                    marginBottom: 0,
                    color:
                      participantIsFinishedStatus === 'done'
                        ? theme.colors.green
                        : theme.colors.black,
                  }}
                >
                  {participantIsFinishedStatus === 'writing' ? (
                    <>
                      <T keyName="Writing" defaultValue="Writing" />{' '}
                      <LoadingDots>...</LoadingDots>
                    </>
                  ) : participantIsFinishedStatus === 'notStarted' ? (
                    'Not yet started'
                  ) : (
                    <>
                      <CheckSvg /> <T keyName="Done" defaultValue="Done" />
                    </>
                  )}
                </p>
              </QuestionMobile>
            )}
          </>
        </ResponseTextContainer>
        {(slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers') &&
          aiAssessedScore !== undefined && (
            <AiAssessedAnswersListItem
              response={response}
              aiAssessedScore={aiAssessedScore}
              isFetchingAiAssessedScores={isFetchingAiAssessedScores}
              gameId={gameId}
              slideId={slideId}
              participantName={participant && (participant.name || participant.nickName)}
            />
          )}
      </ResponseWrapper>
    </ResponseItemContainer>
  );
};

const HorizontalContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
`;

const ResponseItemContainer = styled.li<{
  $dismissed?: boolean;
  $isAiAssessedAnswers: boolean;
}>`
  width: 100%;
  display: flex;
  background-color: ${theme.colors.white};
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  flex-direction: column;
  height: fit-content;
  padding: 10px 20px 20px 20px;
  & > * {
    margin-top: 5px;
    margin-bottom: 5px;
  }
  ${({ $dismissed }) =>
    $dismissed &&
    css`
      background-color: rgba(255, 255, 255, 0.5);
      box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
      transform: scale(0.95);
      opacity: 0.5;
    `}
  ${({ $isAiAssessedAnswers }) =>
    $isAiAssessedAnswers &&
    css`
      background-color: ${theme.colors.background};
      box-shadow: none;
      padding: 0;
      height: 100%;

      @media (max-width: 900px) {
        height: fit-content;
      }
    `}
`;
const ResponseTextContainer = styled.div<{
  $dismissed?: boolean;
  $isAiAssessedAnswers: boolean;
  $aiAssessedScore: AiAssessedScore | undefined;
}>`
  width: 100%;
  height: fit-content;
  ${({ $dismissed, $isAiAssessedAnswers }) =>
    $dismissed &&
    $isAiAssessedAnswers &&
    css`
      background-color: rgba(255, 255, 255, 0.5);
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
      transform: scale(0.95);
    `}
  ${({ $isAiAssessedAnswers }) =>
    $isAiAssessedAnswers &&
    css`
      width: 100%;
      padding: 20px;
      padding-bottom: 5px;
      background-color: ${theme.colors.white};
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
      border-radius: 20px;
      margin-bottom: 10px;
      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
      background-color: ${theme.colors.white};
      box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.1);
    `}
`;

const ResponseWrapper = styled.div<{
  $isAiAssessedAnswers: boolean;
}>`
  display: flex;
  flex-direction: column;
  height: fit-content;

  ${({ $isAiAssessedAnswers }) =>
    $isAiAssessedAnswers &&
    css`
      gap: 10px;
      display: grid;
      margin: 0;
      height: 100%;
    `}
`;

const QuestionMobile = styled.div<{ $borderColor?: string }>`
  span {
    color: ${theme.colors.black};
    font-weight: bold;
    font-size: 24px;
  }
`;
const PlaceMobile = styled.div`
  span {
    color: ${theme.colors.black};
    font-weight: bold;
    font-size: 36px;
  }

  @media (max-width: 1000px) {
    span {
      font-size: 24px;
    }
  }
`;

export const LoadingDots = styled.p`
  font-weight: bold;
  margin: 0;
  display: inline-block;
  clip-path: inset(0 1.2ch 0 0);
  animation: l 1s steps(4) infinite;

  @keyframes l {
    to {
      clip-path: inset(0 -0.3ch 0 0);
    }
  }
`;

const ParticipantsList = ({
  participants,
  currentSelectedStudentId,
  setCurrentSelectedStudentId,
  findParticipantResponse,
  responseCharacterLimit,
  slideType,
  findParticipantIsFinishedStatus,
}: {
  participants: PartialParticipant[];
  currentSelectedStudentId: string | undefined;
  setCurrentSelectedStudentId: (currentSelectedStudentId: string) => void;
  findParticipantResponse: (responseUid: string | undefined) => Response | undefined;
  responseCharacterLimit: number | null | undefined;
  slideType: SlideType;
  findParticipantIsFinishedStatus: (
    participantId: string,
  ) =>
    | 'writing'
    | 'done'
    | 'notStarted'
    | 'dismissed'
    | 'feedbackLoading'
    | 'feedbackReady';
}) => {
  const handleArrowKeyPress = (direction: 'up' | 'down') => {
    if (!currentSelectedStudentId) return;
    const currentIndex = participants.findIndex(p => p.id === currentSelectedStudentId);
    if (direction === 'up') {
      if (currentIndex === 0) {
        setCurrentSelectedStudentId(participants[participants.length - 1].id);
      } else {
        setCurrentSelectedStudentId(participants[currentIndex - 1].id);
      }
    } else {
      if (currentIndex === participants.length - 1) {
        setCurrentSelectedStudentId(participants[0].id);
      } else {
        setCurrentSelectedStudentId(participants[currentIndex + 1].id);
      }
    }
  };

  return (
    <ParticipantsColumn>
      <Hotkeys keyName={'up'} onKeyUp={() => handleArrowKeyPress('up')} />
      <Hotkeys keyName={'down'} onKeyUp={() => handleArrowKeyPress('down')} />
      {participants.map(p => (
        <ParticipantButton
          $status={findParticipantIsFinishedStatus(p.id)}
          $isSelected={currentSelectedStudentId === p.id}
          key={p.id}
          onClick={() => setCurrentSelectedStudentId(p.id)}
        >
          <ParticipantName>{p.name ? p.name : p.nickName}</ParticipantName>
          {findParticipantIsFinishedStatus(p.id) === 'writing' && (
            <div
              style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
            >
              <p>
                <T keyName="Writing" defaultValue="Writing" />{' '}
                <LoadingDots>...</LoadingDots>
              </p>
              <span style={{ fontSize: 14, color: theme.colors.iconGrey }}>
                {slideType === 'answers' &&
                  `${findParticipantResponse(p.id)?.response.length} characters`}

                {slideType === 'aiAssessedAnswers' &&
                  `${findParticipantResponse(p.id)?.response.length} /
                ${responseCharacterLimit || 1000}`}
              </span>
            </div>
          )}
          {findParticipantIsFinishedStatus(p.id) === 'notStarted' && (
            <p>
              <T keyName="Not yet started" defaultValue="Not yet started" />
            </p>
          )}
          {findParticipantIsFinishedStatus(p.id) === 'dismissed' && (
            <p style={{ color: theme.colors.red }}>
              <T keyName="Dismissed" defaultValue="Dismissed" />
            </p>
          )}
          {findParticipantIsFinishedStatus(p.id) === 'feedbackLoading' && (
            <p style={{ color: theme.colors.orange }}>
              <T keyName="Generating feedback" defaultValue="Generating feedback" />{' '}
              <LoadingDots>...</LoadingDots>
            </p>
          )}
          {findParticipantIsFinishedStatus(p.id) === 'feedbackReady' && (
            <p
              style={{
                color: theme.colors.purple,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <CheckSvg /> <T keyName="Feedback ready" defaultValue="Feedback ready" />
            </p>
          )}
          {findParticipantIsFinishedStatus(p.id) === 'done' && (
            <div
              style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
            >
              <p
                style={{
                  color: theme.colors.green,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <CheckSvg /> <T keyName="Done" defaultValue="Done" />
              </p>
              <span style={{ fontSize: 14, color: theme.colors.iconGrey }}>
                {slideType === 'answers' &&
                  `${findParticipantResponse(p.id)?.response.length} characters`}
                {slideType === 'aiAssessedAnswers' &&
                  `${findParticipantResponse(p.id)?.response.length} /
            ${responseCharacterLimit || 1000}`}
              </span>
            </div>
          )}
        </ParticipantButton>
      ))}
    </ParticipantsColumn>
  );
};

const StreamingLongAnswerList = ({
  responses,
  participantsById,
  onDismiss,
  slideType,
  requireRealNames,
  gameId,
  slideId,
  roundState,
  responseCharacterLimit,
}: {
  slideType: SlideType;
  responses: Response[];
  requireRealNames?: boolean;
  participantsById: ById<PartialParticipant>;
  gameId: string;
  slideId: string;
  responseCharacterLimit: number | null | undefined;
  onDismiss: (data: { responseId: string; dismiss: boolean }) => void;
  roundState?: RoundState;
}) => {
  const { tenantId } = useRouterMatch();
  const { gameAPI } = useContext(SocketContext);
  const queryClient = useQueryClient();

  // Listen for responses:changed events and refetch AI scores
  // (Backend doesn't have AI scores socket subscription, so we poll when responses update)
  useEffect(() => {
    if (
      !gameAPI ||
      !slideId ||
      (slideType !== 'aiAssessedAnswers' && slideType !== 'aiAssessedImageAnswers')
    ) {
      return;
    }

    const subscription = gameAPI.game
      .onRoundResponsesChanged({
        gameId,
        tenantId,
        slideId,
      })
      .subscribe(() => {
        // When responses change, refetch AI scores to pick up new scores from live game
        queryClient.invalidateQueries({
          queryKey: ['aiAssessedScores', gameId, slideId],
        });
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [gameAPI, gameId, tenantId, slideId, slideType, queryClient]);

  // Use only the new hook for AI scores (socket-based fetch, NO auto-generation)
  const {
    aiAssessedScores,
    isFetchingAiAssessedScores,
    isGeneratingNewScores,
  } = useListAllAiAssessedScores({
    gameId,
    tenantId,
    slideId,
    isUsingSockets: true,
    // DON'T pass setShowMediaIsDisabled - we never want curimote to generate!
  });

  const isLargerThan800 = useBreakPoint(800);
  const isLargerThan900 = useBreakPoint(900);
  const isLargerThan1000 = useBreakPoint(1000);

  const sortedParticipants = Object.values(participantsById).sort((a, b) => {
    if (a.name && b.name) {
      return a.name.localeCompare(b.name);
    } else {
      return a.nickName.localeCompare(b.nickName);
    }
  });
  const [currentSelectedStudentId, setCurrentSelectedStudentId] = useState(
    sortedParticipants[0]?.id ? sortedParticipants[0].id : undefined,
  );

  const findParticipantResponse = useCallback(
    (responseUid: string | undefined): Response | undefined => {
      const response = responses.find(r => r.uid === responseUid);
      return response;
    },
    [responses],
  );

  useEffect(() => {
    if (!currentSelectedStudentId && sortedParticipants[0]?.id) {
      setCurrentSelectedStudentId(sortedParticipants[0]?.id);
    }
  }, [currentSelectedStudentId, sortedParticipants]);

  const currentStudentResponse = useMemo(() => {
    if (!currentSelectedStudentId) return undefined;
    return findParticipantResponse(currentSelectedStudentId);
  }, [currentSelectedStudentId, findParticipantResponse]);

  const findAiAssessedScore = useCallback(
    (responseId: string) => {
      if (
        roundState === 'post-between' &&
        (slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers') &&
        aiAssessedScores
      ) {
        const aiAssessedScore = aiAssessedScores.find(s => s.responseId === responseId);
        return aiAssessedScore;
      }
      return undefined;
    },
    [aiAssessedScores, roundState, slideType],
  );

  const currentAiAssessedScore = useMemo(() => {
    if (!currentStudentResponse) return undefined;
    return findAiAssessedScore(currentStudentResponse.id);
  }, [currentStudentResponse, findAiAssessedScore]);

  const isAiAssessedSlide =
    slideType === 'aiAssessedAnswers' || slideType === 'aiAssessedImageAnswers';
  const shouldShowAiFeedback =
    isAiAssessedSlide && (roundState === 'post-between' || roundState === 'post');

  const findParticipantIsFinishedStatus = useCallback(
    (
      participantId: string,
    ):
      | 'dismissed'
      | 'writing'
      | 'done'
      | 'notStarted'
      | 'feedbackLoading'
      | 'feedbackReady' => {
      const response = findParticipantResponse(participantId);
      if (!response) return 'notStarted';

      // Dismissed takes precedence over all other states
      if (response.dismissed) return 'dismissed';

      // While actively writing
      if (!response.isFinished && !shouldShowAiFeedback) return 'writing';

      // After writing finishes: show feedback progress (only for AI assessed slides)
      if (shouldShowAiFeedback && isAiAssessedSlide) {
        const generationStarted =
          isFetchingAiAssessedScores ||
          isGeneratingNewScores ||
          (aiAssessedScores && aiAssessedScores.length > 0);

        if (generationStarted) {
          const hasScore = Boolean(
            aiAssessedScores?.some(s => s.responseId === response.id),
          );
          return hasScore ? 'feedbackReady' : 'feedbackLoading';
        }
      }

      return response.isFinished ? 'done' : 'writing';
    },
    [
      aiAssessedScores,
      findParticipantResponse,
      isAiAssessedSlide,
      isFetchingAiAssessedScores,
      isGeneratingNewScores,
      shouldShowAiFeedback,
    ],
  );

  const isMobileView = useMemo(() => {
    return !isLargerThan800 || (!isLargerThan900 && currentAiAssessedScore)
      ? true
      : false;
  }, [isLargerThan800, isLargerThan900, currentAiAssessedScore]);

  //usememo to return all responses in alphabetical order by name or nickname if name is not available
  const sortedResponses = useMemo(() => {
    return responses.sort((a, b) => {
      const aParticipant = participantsById[a.uid];
      const bParticipant = participantsById[b.uid];
      if (aParticipant.name && bParticipant.name) {
        return aParticipant.name.localeCompare(bParticipant.name);
      } else {
        return aParticipant.nickName.localeCompare(bParticipant.nickName);
      }
    });
  }, [responses, participantsById]);

  return (
    <>
      <StreamingResponsesLayout
        style={{
          gridTemplateColumns: isMobileView
            ? '1fr'
            : isLargerThan1000
            ? 'minmax(300px, 350px) 1.618fr'
            : isLargerThan800
            ? 'minmax(280px, 300px) 1.618fr'
            : 'minmax(300px, 350px) 1.618fr',
        }}
      >
        {!isMobileView && (
          <ParticipantsList
            participants={sortedParticipants}
            currentSelectedStudentId={currentSelectedStudentId}
            setCurrentSelectedStudentId={setCurrentSelectedStudentId}
            findParticipantResponse={findParticipantResponse}
            responseCharacterLimit={responseCharacterLimit}
            slideType={slideType}
            findParticipantIsFinishedStatus={findParticipantIsFinishedStatus}
          />
        )}
        {isMobileView && (
          <List>
            {sortedResponses.map(r => (
              <ListItem
                key={r.id}
                response={r}
                participantsById={participantsById}
                onDismiss={onDismiss}
                requireRealNames={requireRealNames}
                slideType={slideType}
                participantIsFinishedStatus={findParticipantIsFinishedStatus(r.uid)}
                aiAssessedScore={findAiAssessedScore(r.id)}
                isFetchingAiAssessedScores={isFetchingAiAssessedScores}
                gameId={gameId}
                slideId={slideId}
              />
            ))}
          </List>
        )}
        {!isMobileView && (
          <List
            style={{
              height: currentAiAssessedScore ? '100%' : 'fit-content',
            }}
          >
            {currentStudentResponse && (
              <ListItem
                key={currentStudentResponse.id}
                response={currentStudentResponse}
                participantsById={participantsById}
                onDismiss={onDismiss}
                requireRealNames={requireRealNames}
                slideType={slideType}
                participantIsFinishedStatus={findParticipantIsFinishedStatus(
                  currentStudentResponse.uid,
                )}
                aiAssessedScore={currentAiAssessedScore}
                isFetchingAiAssessedScores={isFetchingAiAssessedScores}
                gameId={gameId}
                slideId={slideId}
              />
            )}
            {currentSelectedStudentId &&
              findParticipantIsFinishedStatus(currentSelectedStudentId) ===
                'notStarted' && (
                <NotStartedResponseEmptyState>
                  {participantsById[currentSelectedStudentId].name ||
                    participantsById[currentSelectedStudentId].nickName}{' '}
                  - not yet started
                </NotStartedResponseEmptyState>
              )}
          </List>
        )}
      </StreamingResponsesLayout>
    </>
  );
};
const List = styled.ul`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 2px 0px;
  grid-gap: 20px;
  justify-content: space-between;
  list-style: none;
  width: 100%;
  margin: 0 auto;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;

  &::before {
    position: absolute;
    content: attr(data-keyword);
  }
`;

const NotStartedResponseEmptyState = styled.div`
  display: flex;
  align-items: center;
  height: fit-content;
  padding: 20px;
  width: 100%;
  font-size: 24px;
  font-weight: bold;
  color: ${theme.colors.iconGrey};
  border: 1px dashed ${theme.colors.iconGrey};
  border-radius: 10px;
  background-color: ${theme.colors.translucentWhite};
  margin-top: 2px;
`;

const ParticipantsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  width: 100%;
  min-width: 280px;
  overflow-y: auto;
  padding-right: 20px;
`;
const ParticipantButton = styled(Button)<{
  $status:
    | 'writing'
    | 'done'
    | 'notStarted'
    | 'absent'
    | 'dismissed'
    | 'feedbackLoading'
    | 'feedbackReady';
  $isSelected: boolean;
}>`
  display: flex;
  width: 100%;
  min-height: 80px;
  flex-direction: column;
  justify-content: space-between;
  align-items: start;
  padding: 20px;
  margin-left: 10px;
  overflow: hidden;
  border: 2px solid
    ${({ $status }) =>
      $status === 'writing'
        ? theme.colors.blue
        : $status === 'done'
        ? theme.colors.green
        : $status === 'notStarted'
        ? theme.colors.grey
        : $status === 'dismissed'
        ? theme.colors.red
        : $status === 'feedbackLoading'
        ? theme.colors.orange
        : $status === 'feedbackReady'
        ? theme.colors.purple
        : theme.colors.lightPurple};
  border-radius: 20px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.black};
  font-size: 20px;
  font-weight: bold;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  p {
    margin: 0;
  }
  p {
    font-size: 16px;
  }

  &:active,
  &:focus,
  &:hover {
    color: ${theme.colors.black};
    background-color: ${({ $status }) =>
      $status === 'writing'
        ? theme.colors.blueLight
        : $status === 'done'
        ? theme.colors.veryLightGreen
        : $status === 'notStarted'
        ? theme.colors.greyLight
        : $status === 'feedbackLoading'
        ? theme.colors.translucentOrange
        : $status === 'feedbackReady'
        ? theme.colors.lightPurple
        : theme.colors.veryLightRed};

    border: 2px solid
      ${({ $status }) =>
        $status === 'writing'
          ? theme.colors.blue
          : $status === 'done'
          ? theme.colors.green
          : $status === 'notStarted'
          ? theme.colors.grey
          : $status === 'feedbackLoading'
          ? theme.colors.orange
          : $status === 'feedbackReady'
          ? theme.colors.lightPurple
          : theme.colors.veryLightRed};
  }

  ${({ $isSelected, $status }) =>
    $isSelected &&
    css`
      background-color: ${$status === 'writing'
        ? theme.colors.blueLight
        : $status === 'done'
        ? theme.colors.veryLightGreen
        : $status === 'notStarted'
        ? theme.colors.greyLight
        : $status === 'feedbackLoading'
        ? theme.colors.orange
        : $status === 'feedbackReady'
        ? theme.colors.lightPurple
        : theme.colors.veryLightRed};
    `}
`;

const ParticipantName = styled.h2`
  font-size: 24px;
  font-weight: bold;
  margin: 0;
  width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
  @media (max-width: 1000px) {
    font-size: 24px;
  }
`;
const StreamingResponsesLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 350px) 1.618fr;
  padding: 0px 20px 0px 10px;
  width: 100%;
  margin: 0 auto;
  height: calc(100vh - 200px);
  overflow: hidden;

  @media (max-width: 800px) {
    padding: 0px 20px;
  }
`;

export default StreamingLongAnswerList;
